import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  BookText, 
  Plus, 
  Search,
  MoreVertical,
  Trash2,
  CheckCircle,
  Clock,
  FileText
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { useSettings } from '../context/SettingsContext';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  posted: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  void: 'bg-slate-50 text-slate-700 border-slate-200'
};

const JournalEntries = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const { currencySymbol } = useSettings();
  
  const [entries, setEntries] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({ entries: [] });
  const [filterStatus, setFilterStatus] = useState('all');

  const statusLabels = {
    pending: t('pages.journalEntries.pending'),
    posted: t('pages.journalEntries.posted'),
    void: t('pages.journalEntries.void')
  };

  useEffect(() => {
    fetchEntries();
    fetchAccounts();
  }, []);

  const fetchEntries = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/accounts/journal`);
      setEntries(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/accounts/chart`);
      setAccounts(res.data.filter(a => a.is_active));
    } catch (err) {
      console.error(err);
    }
  };

  const handleSave = async () => {
    try {
      await axios.post(`${API_URL}/api/accounts/journal`, formData);
      toast.success(t('pages.journalEntries.entryCreated'));
      setShowDialog(false);
      setFormData({ entries: [] });
      fetchEntries();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('pages.journalEntries.failedToCreate'));
    }
  };

  const handlePost = async (id) => {
    try {
      await axios.put(`${API_URL}/api/accounts/journal/${id}/post`);
      toast.success(t('pages.journalEntries.entryPosted'));
      fetchEntries();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('pages.journalEntries.failedToPost'));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('pages.journalEntries.confirmDelete'))) {
      try {
        await axios.delete(`${API_URL}/api/accounts/journal/${id}`);
        toast.success(t('pages.journalEntries.entryDeleted'));
        fetchEntries();
      } catch (err) {
        toast.error(err.response?.data?.detail || t('pages.journalEntries.failedToDelete'));
      }
    }
  };

  const openNew = () => {
    setFormData({ 
      entries: [
        { account_id: '', debit: 0, credit: 0 },
        { account_id: '', debit: 0, credit: 0 }
      ],
      description: '',
      reference: ''
    });
    setShowDialog(true);
  };

  const addLine = () => {
    setFormData({
      ...formData,
      entries: [...(formData.entries || []), { account_id: '', debit: 0, credit: 0 }]
    });
  };

  const updateLine = (index, field, value) => {
    const newEntries = [...formData.entries];
    newEntries[index] = { ...newEntries[index], [field]: value };
    setFormData({ ...formData, entries: newEntries });
  };

  const removeLine = (index) => {
    setFormData({
      ...formData,
      entries: formData.entries.filter((_, i) => i !== index)
    });
  };

  const getAccountName = (id) => accounts.find(a => a.id === id)?.name || '-';

  const totals = {
    debit: (formData.entries || []).reduce((sum, e) => sum + (parseFloat(e.debit) || 0), 0),
    credit: (formData.entries || []).reduce((sum, e) => sum + (parseFloat(e.credit) || 0), 0)
  };

  const filteredEntries = entries.filter(e => 
    filterStatus === 'all' || e.status === filterStatus
  );

  const stats = {
    total: entries.length,
    pending: entries.filter(e => e.status === 'pending').length,
    posted: entries.filter(e => e.status === 'posted').length
  };

  return (
    <div className={`space-y-6 ${isRTL ? 'text-right' : ''}`}>
      <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('pages.journalEntries.title')}</h1>
          <p className="text-muted-foreground">{t('pages.journalEntries.subtitle')}</p>
        </div>
        <Button onClick={openNew} className={`gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}><Plus className="h-4 w-4" /> {t('pages.journalEntries.newEntry')}</Button>
      </div>

      <div className={`grid grid-cols-3 gap-4 ${isRTL ? 'direction-rtl' : ''}`}>
        <Card><CardContent className="pt-6"><div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}><FileText className="h-8 w-8 text-muted-foreground" /><div><div className="text-2xl font-bold">{stats.total}</div><div className="text-sm text-muted-foreground">{t('pages.journalEntries.totalEntries')}</div></div></div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}><Clock className="h-8 w-8 text-amber-500" /><div><div className="text-2xl font-bold">{stats.pending}</div><div className="text-sm text-muted-foreground">{t('pages.journalEntries.pendingEntries')}</div></div></div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}><CheckCircle className="h-8 w-8 text-emerald-500" /><div><div className="text-2xl font-bold">{stats.posted}</div><div className="text-sm text-muted-foreground">{t('pages.journalEntries.postedEntries')}</div></div></div></CardContent></Card>
      </div>

      <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40"><SelectValue placeholder={t('pages.common.filter')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('pages.employees.allStatus')}</SelectItem>
            <SelectItem value="pending">{t('pages.journalEntries.pending')}</SelectItem>
            <SelectItem value="posted">{t('pages.journalEntries.posted')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className={isRTL ? 'text-right' : ''}>{t('pages.journalEntries.date')}</TableHead>
              <TableHead className={isRTL ? 'text-right' : ''}>{t('pages.journalEntries.reference')}</TableHead>
              <TableHead className={isRTL ? 'text-right' : ''}>{t('pages.journalEntries.description')}</TableHead>
              <TableHead className={`${isRTL ? 'text-left' : 'text-right'}`}>{t('pages.journalEntries.debit')}</TableHead>
              <TableHead className={`${isRTL ? 'text-left' : 'text-right'}`}>{t('pages.journalEntries.credit')}</TableHead>
              <TableHead className={isRTL ? 'text-right' : ''}>{t('pages.journalEntries.status')}</TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredEntries.map((entry) => (
              <TableRow key={entry.id}>
                <TableCell className="text-muted-foreground">{format(new Date(entry.date), 'MMM d, yyyy')}</TableCell>
                <TableCell className="font-mono">{entry.reference || '-'}</TableCell>
                <TableCell><div className="max-w-xs truncate">{entry.description}</div></TableCell>
                <TableCell className={`${isRTL ? 'text-left' : 'text-right'} font-mono`}>{currencySymbol} {entry.total_debit?.toLocaleString()}</TableCell>
                <TableCell className={`${isRTL ? 'text-left' : 'text-right'} font-mono`}>{currencySymbol} {entry.total_credit?.toLocaleString()}</TableCell>
                <TableCell><Badge className={statusColors[entry.status]}>{statusLabels[entry.status] || entry.status}</Badge></TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8"><MoreVertical className="h-4 w-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align={isRTL ? 'start' : 'end'}>
                      {entry.status === 'pending' && (
                        <>
                          <DropdownMenuItem onClick={() => handlePost(entry.id)}><CheckCircle className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} /> {t('pages.journalEntries.postEntry')}</DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(entry.id)}>
                            <Trash2 className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} /> {t('pages.common.delete')}
                          </DropdownMenuItem>
                        </>
                      )}
                      {entry.status === 'posted' && (
                        <DropdownMenuItem disabled><CheckCircle className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} /> {t('pages.journalEntries.posted')}</DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
            {filteredEntries.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="h-32 text-center">
                  <BookText className="h-8 w-8 mx-auto text-muted-foreground/30 mb-2" />
                  <p className="text-muted-foreground">{t('pages.journalEntries.noEntries')}</p>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{t('pages.journalEntries.newEntry')}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('pages.journalEntries.date')}</Label>
                <Input type="date" value={formData.date || ''} onChange={e => setFormData({...formData, date: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>{t('pages.journalEntries.reference')}</Label>
                <Input value={formData.reference || ''} onChange={e => setFormData({...formData, reference: e.target.value})} placeholder="JE-001" />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('pages.journalEntries.description')} *</Label>
              <Textarea value={formData.description || ''} onChange={e => setFormData({...formData, description: e.target.value})} rows={2} />
            </div>

            <div className="space-y-2">
              <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Label>{t('pages.journalEntries.entryLines')}</Label>
                <Button type="button" variant="outline" size="sm" onClick={addLine}><Plus className="h-3 w-3 mr-1" /> {t('pages.journalEntries.addLine')}</Button>
              </div>
              <div className="space-y-2">
                <div className={`grid grid-cols-12 gap-2 text-xs font-medium text-muted-foreground px-1 ${isRTL ? 'text-right' : ''}`}>
                  <div className="col-span-6">{t('pages.journalEntries.account')}</div>
                  <div className="col-span-2 text-right">{t('pages.journalEntries.debit')}</div>
                  <div className="col-span-2 text-right">{t('pages.journalEntries.credit')}</div>
                  <div className="col-span-2"></div>
                </div>
                {(formData.entries || []).map((line, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                    <Select className="col-span-6" value={line.account_id} onValueChange={v => updateLine(idx, 'account_id', v)}>
                      <SelectTrigger className="col-span-6"><SelectValue placeholder={t('pages.journalEntries.selectAccount')} /></SelectTrigger>
                      <SelectContent>{accounts.map(a => <SelectItem key={a.id} value={a.id}>{a.code} - {a.name}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input className="col-span-2" type="number" placeholder="0.00" value={line.debit || ''} onChange={e => updateLine(idx, 'debit', parseFloat(e.target.value) || 0)} />
                    <Input className="col-span-2" type="number" placeholder="0.00" value={line.credit || ''} onChange={e => updateLine(idx, 'credit', parseFloat(e.target.value) || 0)} />
                    <Button variant="ghost" size="icon" className="col-span-2" onClick={() => removeLine(idx)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-muted/50 rounded-lg p-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className={`flex justify-between ${isRTL ? 'flex-row-reverse' : ''}`}><span>{t('pages.journalEntries.totalDebit')}:</span><span className="font-mono font-bold">{currencySymbol} {totals.debit.toFixed(2)}</span></div>
                <div className={`flex justify-between ${isRTL ? 'flex-row-reverse' : ''}`}><span>{t('pages.journalEntries.totalCredit')}:</span><span className="font-mono font-bold">{currencySymbol} {totals.credit.toFixed(2)}</span></div>
              </div>
              {Math.abs(totals.debit - totals.credit) > 0.01 && (
                <div className="mt-2 text-sm text-red-600 font-medium">⚠️ {t('pages.journalEntries.mustBalance')}</div>
              )}
            </div>
          </div>
          <DialogFooter className={isRTL ? 'flex-row-reverse' : ''}>
            <Button variant="outline" onClick={() => setShowDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleSave} disabled={Math.abs(totals.debit - totals.credit) > 0.01}>{t('pages.journalEntries.createEntry')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JournalEntries;
